import {Injectable} from '@angular/core';
import {ApibaseService} from '~core/api/apibase.service';
import {Observable, of, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {RotaVisitasIdsVo} from '~model/vo/rota-visitas-ids-vo';
import { HttpClient, HttpParams } from '@angular/common/http';
import {MotoristaVo, RotaVo} from '~model/vo/rota-vo';
import {ResumoVo} from '~model/vo/resumo-vo';
import {ResultMessageVo} from '~model/vo/result-message-vo';
import {RotaCancelVisita} from '~shared/components/cancela-visita/cancela-visita.component';
import {ParamService} from '~core/utils/param.service';
import {RemovePoiResponseVo, ResultRemoverPoiVo} from '~model/vo/result-remover-poi-vo';
import { RotaDetalhesRelatorioVO } from '~model/vo/RelatorioTabelaVO';

@Injectable({
  providedIn: 'root',
})
export class RotaService extends ApibaseService {

  prefixoUrl: string = 'controlTower';
  urlFunction: string = 'rota';

  constructor(
    protected http: HttpClient,
    protected param: ParamService
  ) {
    super(http);
  }

  getRota(id: string): Observable<RotaVo> {
    return super.get<RotaVo>({extraUrl: id}).pipe(this.defaultCatch());
  }

  getRotas(ids: string[]): Observable<RotaVo[]> {
    const paramns = ids.reduce((p, id) => p.append('ids', id || ''), new HttpParams());
    return super.get<RotaVo[]>({paramns}).pipe(this.defaultCatchList());
  }

  getResumo(rotaId: string): Observable<ResumoVo> {
    return super.get<ResumoVo>({extraUrl: `${rotaId}/resumo`}).pipe(this.defaultCatch());
  }

  getTableResumo(rotaId: string): Observable<RotaDetalhesRelatorioVO> {
    return super.get<RotaDetalhesRelatorioVO>({ extraUrl: `tabela-relatorio/${rotaId}` }).pipe(this.defaultCatch());
  }

  getResumosByData(data: string): Observable<ResumoVo[]> {
    return super.get<ResumoVo[]>({extraUrl: `resumo/${data}`}).pipe(this.defaultCatchList());
  }

  cancelaRota(rotaId: string, motivo: string): Observable<ResultMessageVo> {
    return super.put(rotaId, {sufixUrl: 'cancela-rota', objEnvio: motivo}).pipe(
      map(_ => ({message: 'Rota cancelada com sucesso'})),
      catchError((err) => {
        const messages: {[k: number]: string} = {
          404: 'Rota não encontrada',
          400: 'Essa operação não foi permitida',
        };
        const erro = {erro: true, message: messages[err.status] || 'Ocorreu um erro inesperado na operação'};
        return of(erro);
      })
    );
  }

  removeVisitas(rotaCancel: RotaCancelVisita, cancela: boolean): Observable<ResultRemoverPoiVo> {
    const codigo: string = '';
    const sufixUrl = cancela ? 'cancela-visitas' : 'remove-visitas';
    return super.put<RemovePoiResponseVo>(codigo, {extraUrl: `${rotaCancel.id}/visita`, sufixUrl, objEnvio: rotaCancel}).pipe(
      map(res =>
        ({ codigo, message: 'Visitas canceladas com sucesso', ...res })),
      catchError((err) => {
        const messages: {[k: number]: string} = {
          404: 'Visita não encontrada',
          400: 'Essa operação não foi permitida',
        };
        const erro = {codigo, erro: true, message: messages[err.status] || 'Ocorreu um erro inesperado na operação'};
        return of(erro);
      })
    );
  }

  updateOrdem(visitasOrdenacao: RotaVisitasIdsVo): Observable<ResultMessageVo> {
    return super.put<RotaVisitasIdsVo>(visitasOrdenacao.id, {sufixUrl: 'reordena-visitas', objEnvio: visitasOrdenacao}).pipe(
      map(res => {
        let message = 'Reordenação realizada com sucesso!';
        let duration;
        if (res.alerta) {
          message = 'Reordenação realizada, mas ' + res.alerta;
          duration = 0;
        }
        return ({message, duration});
      }),
      catchError((err) => {
        const messages: {[k: number]: string} = {
          404: 'Rota não encontrada',
          400: 'Essa operação não foi permitida',
        };
        const erro = {erro: true, message: messages[err.status] || 'Ocorreu um erro inesperado na operação'};
        return of(erro);
      })
    );
  }

  getCoresDisponiveis(): Observable<string[]> {
    return super.get<string[]>({extraUrl: `cores/${this.param.dataMonitoramento}`}).pipe(this.defaultCatchList());
  }

  alterarCor(rotaId: string, cor: string): Observable<RotaVo> {
    return super.put<RotaVo>(rotaId, {extraUrl: 'alterarCor', objEnvio: cor});
  }

  reverterStatus(idRota: string, visita: string, servico: string): Observable<ResultMessageVo> {
    const codigo: string = '';
    const sufixUrl = 'reverter-status';
    return super.put<ResultMessageVo>(codigo, {
      extraUrl: `${idRota}/visita/${visita}/${servico}`,
      sufixUrl,
    }).pipe(
      map(() => {
        // Retorna um objeto do tipo ResultMessageVo em caso de sucesso
        return {
          codigo,
          message: 'Status revertido com sucesso',
          erro: false,
        } as ResultMessageVo;
      }),
      catchError((err) => {
        const messages: { [k: number]: string } = {
          404: 'Visita ou serviço não encontrado',
          400: 'Operação não permitida',
        };
        const erro = { erro: true, message: messages[err.status] || 'Ocorreu um erro inesperado na operação' };
        return of(erro);
      })
    );
  }

  getMotoristasAtivos(): Observable<MotoristaVo[]> {
    return super
      .get<MotoristaVo[]>({ extraUrl: 'motoristas/ativos' })
      .pipe(this.defaultCatchList());
  }

}
